import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * Quick-Create Dialog (New Task List / New Custom Table / Publishing Schedule).
 * Extracted from AppLayout.js — pure UI, no behavior changes.
 *
 * Props:
 *   state:     { kind, parent_kind, parent_id, parent_name } | null
 *   onClose:   () => void
 *   onCreated: (createdRow) => void
 */
export default function QuickCreateDialog({ state, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (state) {
      setName(state.kind === "task_list" ? "New Task List" : state.kind === "publishing_schedule" ? "Publishing Schedule" : "New Table");
      setDescription("");
    }
  }, [state]);
  if (!state) return null;
  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/sidebar-lists", {
        kind: state.kind,
        name: name.trim(),
        description: description.trim(),
        parent_kind: state.parent_kind,
        parent_id: state.parent_id,
      });
      toast.success("Created");
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="quick-create-dialog">
        <DialogHeader>
          <DialogTitle>
            {state.kind === "task_list" ? "New Task List" : "New Custom Table"}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-0.5">under <span className="font-medium text-slate-700">{state.parent_name}</span></div>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="quick-create-name" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1">Description (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="quick-create-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="quick-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-purple-600 hover:bg-purple-700" data-testid="quick-create-submit">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
